import { useNostr } from '@nostrify/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';
import type { NostrEvent } from '@nostrify/nostrify';

import { useCurrentUser } from './useCurrentUser';
import { useNostrPublish } from './useNostrPublish';
import {
  DEFAULT_LIST_D,
  GROCERY_LIST_KIND,
  MAX_ITEM_NAME_LENGTH,
  MAX_ITEM_QTY_LENGTH,
  type GroceryItem,
  attributeItemsToAuthor,
  getListTitle,
  getParticipants,
  isValidPubkey,
  mergeItems,
  newItemId,
  parseItemsJson,
  resolveEncryptionPeer,
  sanitizeFreeText,
} from '@/lib/grocery';

/** A list event together with its decrypted items, for cache friendliness. */
interface DecryptedList {
  event: NostrEvent;
  items: GroceryItem[];
  /** True if the event content was readable; false if decryption failed. */
  decrypted: boolean;
}

interface UseGroceryListResult {
  items: GroceryItem[];
  title: string;
  participants: string[];
  myEvent: NostrEvent | undefined;
  events: NostrEvent[];
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  /** True when at least one event was found but couldn't be decrypted. */
  hasDecryptionError: boolean;
  /** True if the active signer doesn't expose NIP-44. */
  signerSupportsEncryption: boolean;
  refetch: () => void;
  addItem: (input: { name: string; qty?: string }) => Promise<void>;
  toggleItem: (id: string) => Promise<void>;
  removeItem: (id: string) => Promise<void>;
  clearChecked: () => Promise<void>;
  setParticipant: (pubkey: string | null) => Promise<void>;
  renameList: (title: string) => Promise<void>;
  isMutating: boolean;
}

/**
 * Hook that loads + maintains the user's shared grocery list (kind 36899).
 *
 * **Encryption.** Items are stored as a NIP-44 ciphertext in `event.content`.
 * - When a partner is tagged, the author encrypts to that partner's pubkey.
 *   ECDH is symmetric, so both author and partner can decrypt.
 * - With no partner, the author encrypts to themselves so the list is still
 *   private from any third party that fetches the event.
 *
 * The `p` tag (partner) and `d` tag (list id) are still publicly visible,
 * because relays need them as filters. Item names, quantities, and check
 * state are not.
 */
export function useGroceryList(d: string = DEFAULT_LIST_D): UseGroceryListResult {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const { mutateAsync: publish, isPending: isPublishing } = useNostrPublish();
  const queryClient = useQueryClient();

  const myPubkey = user?.pubkey;
  const signer = user?.signer;
  const signerSupportsEncryption = !!signer?.nip44;

  /** Decrypt an event's content. Returns `null` if it couldn't be decrypted. */
  const decryptEvent = useCallback(
    async (event: NostrEvent): Promise<DecryptedList> => {
      // Empty content = empty list. Nothing to decrypt.
      if (!event.content) {
        return { event, items: [], decrypted: true };
      }

      if (!signer?.nip44 || !myPubkey) {
        return { event, items: [], decrypted: false };
      }

      try {
        const peer = resolveEncryptionPeer(event, myPubkey);
        const plaintext = await signer.nip44.decrypt(peer, event.content);
        // Stamp every item's authorship to the event's pubkey. The fields
        // inside the encrypted JSON are written by whoever holds that key,
        // so the only authorship we can actually prove is "this came from
        // event.pubkey". See `attributeItemsToAuthor` for the rationale.
        const items = attributeItemsToAuthor(parseItemsJson(plaintext), event.pubkey);
        return { event, items, decrypted: true };
      } catch (err) {
        // Most common cause: the event was encrypted with a different peer
        // than we expected (e.g. user changed partners), or the signer
        // refused. Fall back to an empty list rather than crashing the UI.
        console.warn('Failed to decrypt grocery list event', err);
        return { event, items: [], decrypted: false };
      }
    },
    [signer, myPubkey],
  );

  // Step 1: load this user's own list event so we know who their partner is.
  const ownQuery = useQuery({
    queryKey: ['grocery-list', 'own', myPubkey, d],
    enabled: !!myPubkey && !!signer,
    queryFn: async (c): Promise<DecryptedList | undefined> => {
      const events = await nostr.query(
        [{ kinds: [GROCERY_LIST_KIND], authors: [myPubkey!], '#d': [d], limit: 1 }],
        { signal: AbortSignal.any([c.signal, AbortSignal.timeout(8000)]) },
      );
      const latest = events.sort((a, b) => b.created_at - a.created_at)[0];
      if (!latest) return undefined;
      return decryptEvent(latest);
    },
  });

  const myEntry = ownQuery.data;
  const myEvent = myEntry?.event;

  const partner = useMemo(() => {
    if (!myEvent) return undefined;
    return getParticipants(myEvent)[0];
  }, [myEvent]);

  // Step 2: load the partner's list event (same `d`) so we can merge it in.
  const partnerQuery = useQuery({
    queryKey: ['grocery-list', 'partner', partner, myPubkey, d],
    enabled: !!partner && !!myPubkey && !!signer,
    queryFn: async (c): Promise<DecryptedList | undefined> => {
      const events = await nostr.query(
        [{ kinds: [GROCERY_LIST_KIND], authors: [partner!], '#d': [d], limit: 1 }],
        { signal: AbortSignal.any([c.signal, AbortSignal.timeout(8000)]) },
      );
      // Reciprocal-tagging trust check (also: this is what tells the partner
      // event how to derive the encryption peer — they must have tagged us
      // for the ECDH math to line up with our key).
      const reciprocal = events
        .filter((e) => myPubkey && getParticipants(e).includes(myPubkey))
        .sort((a, b) => b.created_at - a.created_at)[0];
      if (!reciprocal) return undefined;
      return decryptEvent(reciprocal);
    },
  });

  const partnerEntry = partnerQuery.data;
  const partnerEvent = partnerEntry?.event;

  /**
   * Live subscription. Opens a long-lived `nostr.req()` to relays for the
   * union of {me, partner} authors filtered to our `d`-tagged kind 36899
   * events. When a fresher event arrives — e.g. your wife checks off the
   * milk on her phone — we decrypt it and patch the corresponding query
   * cache, so the UI updates without anyone hitting refresh.
   *
   * Notes:
   * - The subscription is restarted whenever `myPubkey` or `partner`
   *   changes (different filter shape).
   * - We ignore events that aren't strictly newer than what's already in
   *   the cache, so a stale relay echo can't undo a just-published edit.
   * - Background tabs throttle WebSockets, so the subscription may pause.
   *   `refetchOnWindowFocus` is the backstop when the tab is reactivated.
   */
  useEffect(() => {
    if (!myPubkey || !signer) return;
    const authors = partner ? [myPubkey, partner] : [myPubkey];
    const controller = new AbortController();

    (async () => {
      try {
        for await (const msg of nostr.req(
          [{ kinds: [GROCERY_LIST_KIND], authors, '#d': [d] }],
          { signal: controller.signal },
        )) {
          if (controller.signal.aborted) break;
          if (msg[0] !== 'EVENT') continue; // ignore EOSE/CLOSED

          const event = msg[2];

          // Branch by author so we update the right cache key.
          const isOwn = event.pubkey === myPubkey;
          const isPartner = !!partner && event.pubkey === partner;
          if (!isOwn && !isPartner) continue;

          // For partner events we still require reciprocal tagging — same
          // trust check the queryFn does.
          if (isPartner && !getParticipants(event).includes(myPubkey)) {
            continue;
          }

          const queryKey = isOwn
            ? (['grocery-list', 'own', myPubkey, d] as const)
            : (['grocery-list', 'partner', partner, myPubkey, d] as const);

          const existing =
            queryClient.getQueryData<DecryptedList | undefined>(queryKey);

          // Skip stale echoes. Replaceable events should monotonically
          // advance; anything older or equal can be ignored.
          if (existing && existing.event.created_at >= event.created_at) {
            continue;
          }

          // Decryption is async and can block on the signer; bail if we've
          // been aborted in the meantime.
          const decrypted = await decryptEvent(event);
          if (controller.signal.aborted) break;

          queryClient.setQueryData(queryKey, decrypted);
        }
      } catch (err) {
        if (controller.signal.aborted) return;
        // Connection drops, relay errors, etc. We don't surface these in
        // the UI — the manual refresh button and refetchOnWindowFocus
        // remain as fallbacks.
        console.warn('Grocery list subscription ended', err);
      }
    })();

    return () => controller.abort();
  }, [nostr, queryClient, decryptEvent, myPubkey, signer, partner, d]);

  const events = useMemo(() => {
    const all: NostrEvent[] = [];
    if (myEvent) all.push(myEvent);
    if (partnerEvent) all.push(partnerEvent);
    return all;
  }, [myEvent, partnerEvent]);

  const items = useMemo(() => {
    const sets: GroceryItem[][] = [];
    if (myEntry) sets.push(myEntry.items);
    if (partnerEntry) sets.push(partnerEntry.items);
    return mergeItems(sets);
  }, [myEntry, partnerEntry]);

  const titleSource = myEvent ?? partnerEvent;
  const title = (titleSource && getListTitle(titleSource)) ?? 'Groceries';
  const participants = myEvent ? getParticipants(myEvent) : [];

  const hasDecryptionError =
    (myEntry && !myEntry.decrypted) || (partnerEntry && !partnerEntry.decrypted) || false;

  const isLoading = ownQuery.isLoading || (!!partner && partnerQuery.isLoading);
  const isFetching = ownQuery.isFetching || partnerQuery.isFetching;

  const refetch = useCallback(() => {
    ownQuery.refetch();
    partnerQuery.refetch();
  }, [ownQuery, partnerQuery]);

  /**
   * Publish a new version of the user's own list event. We mutate only our
   * own copy; the partner publishes their own.
   */
  const publishMutation = useMutation({
    mutationFn: async (next: {
      items: GroceryItem[];
      title?: string;
      partnerPubkey?: string | null;
    }): Promise<DecryptedList> => {
      if (!myPubkey) throw new Error('Not logged in');
      if (!signer?.nip44) {
        throw new Error(
          'Your signer does not support NIP-44 encryption. Please upgrade your Nostr extension.',
        );
      }

      const resolvedTitle =
        next.title ?? (myEvent && getListTitle(myEvent)) ?? 'Groceries';

      // Resolve partner: explicit override -> existing partner -> none.
      // Defense in depth — refuse to pair with ourselves (the UI also
      // blocks this in SharePartnerDialog, but we don't want a stray hook
      // call to silently degrade encryption to "encrypted to self" while
      // the UI shows a heart-shaped "Sharing with You" pill).
      const candidate =
        next.partnerPubkey === null
          ? null
          : (next.partnerPubkey ?? partner ?? null);
      const resolvedPartner =
        candidate && candidate !== myPubkey && isValidPubkey(candidate)
          ? candidate
          : null;

      // Encrypt to the partner if we have one, otherwise to ourselves.
      const peer = resolvedPartner ?? myPubkey;
      const plaintext = JSON.stringify(next.items);
      const ciphertext = await signer.nip44.encrypt(peer, plaintext);

      const tags: string[][] = [
        ['d', d],
        ['title', resolvedTitle],
        ['alt', 'Encrypted shared grocery list'],
        ['encrypted'],
      ];
      if (resolvedPartner) tags.push(['p', resolvedPartner]);

      const event = await publish({
        kind: GROCERY_LIST_KIND,
        content: ciphertext,
        tags,
      });
      // Return the decrypted form so we can prime the cache without doing a
      // second decrypt round-trip to the signer.
      return { event, items: next.items, decrypted: true };
    },
    onSuccess: (entry) => {
      queryClient.setQueryData(
        ['grocery-list', 'own', myPubkey, d],
        entry,
      );
    },
  });

  const ourItems = useCallback(
    (): GroceryItem[] => myEntry?.items ?? [],
    [myEntry],
  );

  const addItem: UseGroceryListResult['addItem'] = useCallback(
    async ({ name, qty }) => {
      if (!myPubkey) return;
      // Strip control characters (zero-width chars, RTL overrides, etc.)
      // and clamp length. Anything paste-bombed beyond the limit is
      // silently truncated rather than rejected — easier UX than a popup.
      const cleanName = sanitizeFreeText(name, MAX_ITEM_NAME_LENGTH);
      if (!cleanName) return;
      const cleanQty = qty
        ? sanitizeFreeText(qty, MAX_ITEM_QTY_LENGTH) || undefined
        : undefined;
      const now = Math.floor(Date.now() / 1000);
      const next: GroceryItem[] = [
        {
          id: newItemId(),
          name: cleanName,
          qty: cleanQty,
          checked: false,
          addedBy: myPubkey,
          addedAt: now,
          updatedBy: myPubkey,
          updatedAt: now,
        },
        ...ourItems(),
      ];
      await publishMutation.mutateAsync({ items: next });
    },
    [myPubkey, ourItems, publishMutation],
  );

  const toggleItem: UseGroceryListResult['toggleItem'] = useCallback(
    async (id) => {
      if (!myPubkey) return;
      const now = Math.floor(Date.now() / 1000);

      const ours = ourItems();
      const inOurs = ours.find((i) => i.id === id);

      if (inOurs) {
        const next = ours.map((i) =>
          i.id === id
            ? { ...i, checked: !i.checked, updatedBy: myPubkey, updatedAt: now }
            : i,
        );
        await publishMutation.mutateAsync({ items: next });
        return;
      }

      // The item belongs to the partner. Write an updated copy into our
      // event so our last-write-wins record overrides theirs.
      const merged = items.find((i) => i.id === id);
      if (!merged) return;
      const next = [
        { ...merged, checked: !merged.checked, updatedBy: myPubkey, updatedAt: now },
        ...ours,
      ];
      await publishMutation.mutateAsync({ items: next });
    },
    [items, myPubkey, ourItems, publishMutation],
  );

  const removeItem: UseGroceryListResult['removeItem'] = useCallback(
    async (id) => {
      if (!myPubkey) return;
      const ours = ourItems();
      const inOurs = ours.find((i) => i.id === id);
      // Only allow removing items from our own copy. Partner-owned items can
      // be checked off but not hard-deleted from this side.
      if (!inOurs) return;
      const next = ours.filter((i) => i.id !== id);
      await publishMutation.mutateAsync({ items: next });
    },
    [myPubkey, ourItems, publishMutation],
  );

  const clearChecked: UseGroceryListResult['clearChecked'] = useCallback(async () => {
    if (!myPubkey) return;
    const ours = ourItems();
    const next = ours.filter((i) => !i.checked);
    if (next.length === ours.length) return;
    await publishMutation.mutateAsync({ items: next });
  }, [myPubkey, ourItems, publishMutation]);

  const setParticipant: UseGroceryListResult['setParticipant'] = useCallback(
    async (pubkey) => {
      if (!myPubkey) return;
      // Note: when we change partners, our existing items get re-encrypted
      // to the new peer. Items originally added by the *old* partner are
      // gone from our copy by design (we never wrote them into our event),
      // and the old partner's event is no longer reachable since we drop
      // the `p` tag.
      await publishMutation.mutateAsync({ items: ourItems(), partnerPubkey: pubkey });
    },
    [myPubkey, ourItems, publishMutation],
  );

  const renameList: UseGroceryListResult['renameList'] = useCallback(
    async (newTitle) => {
      if (!myPubkey) return;
      await publishMutation.mutateAsync({ items: ourItems(), title: newTitle });
    },
    [myPubkey, ourItems, publishMutation],
  );

  return {
    items,
    title,
    participants,
    myEvent,
    events,
    isLoading,
    isFetching,
    error: (ownQuery.error ?? partnerQuery.error) as Error | null,
    hasDecryptionError,
    signerSupportsEncryption,
    refetch,
    addItem,
    toggleItem,
    removeItem,
    clearChecked,
    setParticipant,
    renameList,
    isMutating: isPublishing || publishMutation.isPending,
  };
}
